import { useNavigate } from "react-router-dom";
import { Bell, Volume2, Square, Play, CalendarClock, Compass } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LangContext";
import { muezzinById } from "@/lib/prayerTimes";
import { LANG_TAG } from "@/lib/geo";
import { fill } from "@/lib/utils";
import { IFTAR_DUA, isFastingToday } from "@/lib/fasting";

// Modal shown app-wide when a prayer time arrives; controls the shared adhan player.
export function PrayerAlertDialog({ alert, label, adhan, muezzinId, onClose }) {
  const { tc, lang } = useLang();
  const navigate = useNavigate();
  if (!alert) return null;
  const time = new Date(alert.at).toLocaleTimeString(LANG_TAG[lang] || "en-US", { hour: "2-digit", minute: "2-digit" });
  const close = () => { onClose(); };
  const go = (to) => { onClose(); navigate(to); };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-sm overflow-hidden border-[hsl(var(--gold))]/40 p-0" data-testid="prayer-alert-dialog" data-prayer={alert.key}>
        <div className="bg-primary px-6 pb-6 pt-8 text-center text-primary-foreground">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/30">
            <Bell className="h-7 w-7 animate-[pulse_1.5s_ease-in-out_infinite]" />
          </span>
          <DialogHeader className="mt-4 space-y-1 text-center sm:text-center">
            <DialogDescription className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--gold))]">
              {tc({ id: "Waktu salat telah tiba", en: "It's prayer time", ar: "حان وقت الصلاة" })}
            </DialogDescription>
            <DialogTitle className="font-serif text-4xl text-white" data-testid="prayer-alert-title">{label}</DialogTitle>
          </DialogHeader>
          <p className="mt-1 text-sm text-white/80 tabular-nums" data-testid="prayer-alert-time">{time}</p>
          <p className="mt-3 text-sm text-white/90 leading-relaxed">
            {alert.key === "fajr"
              ? tc({ id: "Salat lebih baik daripada tidur — mulailah hari dengan Subuh berjamaah.", en: "Prayer is better than sleep — begin your day with Fajr in congregation.", ar: "الصلاة خير من النوم — ابدأ يومك بصلاة الفجر في جماعة." })
              : tc({ id: "Tinggalkan kesibukan sejenak, sempurnakan wudhu, dan hadapkan hati kepada Allah.", en: "Pause for a moment, perfect your wudu, and turn your heart to Allah.", ar: "توقّف لحظة، وأتمّ وضوءك، وتوجّه بقلبك إلى الله." })}
          </p>
        </div>

        <div className="space-y-3 px-6 py-5">
          {alert.key === "maghrib" && isFastingToday() && (
            <div className="rounded-2xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/10 p-4 text-center" data-testid="prayer-alert-iftar" dir="auto">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--gold))]">{tc({ id: "Selamat berbuka — doa berbuka puasa", en: "Time to break the fast — iftar du'a", ar: "أفطر على خير — دعاء الإفطار" })}</p>
              <p className="mt-2 font-arabic text-2xl leading-loose" dir="rtl" lang="ar">{IFTAR_DUA.arabic}</p>
              <p className="mt-1 text-xs italic text-muted-foreground">{IFTAR_DUA.latin}</p>
              <p className="mt-1 text-sm leading-relaxed">{tc(IFTAR_DUA.meaning)}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{tc(IFTAR_DUA.source)}</p>
            </div>
          )}
          <div className="flex items-center gap-2 rounded-2xl bg-secondary/70 px-3 py-2 text-xs text-muted-foreground" data-testid="prayer-alert-adhan" data-playing={adhan.playing || undefined}>
            <Volume2 className="h-4 w-4 shrink-0 text-primary" />
            <span className="flex-1 truncate">
              {adhan.playing
                ? fill(tc({ id: "Adzan {prayer} berkumandang…", en: "{prayer} adhan is playing…", ar: "أذان {prayer} يُرفع الآن…" }), { prayer: label })
                : adhan.blocked
                  ? tc({ id: "Browser memblokir putar otomatis — ketuk untuk memutar adzan", en: "Autoplay was blocked — tap to play the adhan", ar: "منع المتصفح التشغيل التلقائي — اضغط لتشغيل الأذان" })
                  : tc(muezzinById(muezzinId).label)}
            </span>
            {adhan.playing ? (
              <button onClick={adhan.stop} data-testid="prayer-alert-stop" className="inline-flex h-8 items-center gap-1 rounded-full bg-primary px-3 text-xs font-medium text-white hover:opacity-90">
                <Square className="h-3 w-3 fill-white" />{tc({ id: "Hentikan", en: "Stop", ar: "إيقاف" })}
              </button>
            ) : (
              <button onClick={() => adhan.play(alert.key, muezzinId)} data-testid="prayer-alert-play" className={`inline-flex h-8 items-center gap-1 rounded-full px-3 text-xs font-medium text-white hover:opacity-90 ${adhan.blocked ? "bg-amber-500 animate-pulse" : "bg-primary"}`}>
                <Play className="h-3 w-3 fill-white" />{tc({ id: "Putar adzan", en: "Play adhan", ar: "تشغيل الأذان" })}
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => go("/times")} data-testid="prayer-alert-times">
              <CalendarClock className="h-4 w-4 me-1.5" />{tc({ id: "Jadwal salat", en: "Prayer times", ar: "مواقيت الصلاة" })}
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => go("/")} data-testid="prayer-alert-qibla">
              <Compass className="h-4 w-4 me-1.5" />{tc({ id: "Arah kiblat", en: "Qibla", ar: "اتجاه القبلة" })}
            </Button>
          </div>
          <Button className="w-full rounded-full" onClick={close} data-testid="prayer-alert-close">
            {tc({ id: "Tutup", en: "Dismiss", ar: "إغلاق" })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
